import React from 'react';
import { Box } from '@mui/material';
import { Markdown } from '@qwickapps/react-framework';
import { useDocument } from '../contexts/DocumentContext';
import { LetterheadHeader } from './LetterheadHeader';
import { LetterheadFooter } from './LetterheadFooter';

export const MarkdownPreview: React.FC = () => {
  const { content, typographyStyle, letterheadConfig } = useDocument();

  const typographyStyles = {
    fontFamily: typographyStyle.fontFamily,
    fontSize: `${typographyStyle.fontSize}px`,
    lineHeight: typographyStyle.lineHeight,
    '& > *:first-of-type': {
      marginTop: '0 !important',
    },
    '& p': {
      marginTop: 0,
      marginBottom: `${typographyStyle.paragraphSpacing}em`,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: `${typographyStyle.headingTopMargin}em`,
      marginBottom: `${typographyStyle.headingBottomMargin}em`,
    },
    '& h1': {
      fontSize: `${typographyStyle.fontSize * typographyStyle.headingScale * 2}px`,
      marginTop: `${typographyStyle.sectionSpacing}em`,
    },
    '& h1:first-of-type': {
      marginTop: '0 !important',
    },
    '& h2': {
      fontSize: `${typographyStyle.fontSize * typographyStyle.headingScale * 1.5}px`,
      marginTop: `${typographyStyle.sectionSpacing * 0.8}em`,
    },
    '& h3': {
      fontSize: `${typographyStyle.fontSize * typographyStyle.headingScale * 1.25}px`,
    },
    '& h4, & h5, & h6': {
      fontSize: `${typographyStyle.fontSize * typographyStyle.headingScale}px`,
    },
    '& table': {
      borderCollapse: 'collapse !important',
      width: '100% !important',
      marginTop: `${typographyStyle.paragraphSpacing}em !important`,
      marginBottom: `${typographyStyle.paragraphSpacing}em !important`,
    },
    '& table th, & table td': {
      border: typographyStyle.tableBorders !== false ? '1px solid #ddd !important' : 'none !important',
      padding: '8px 12px !important',
      textAlign: 'left !important',
    },
    '& table th': {
      backgroundColor: typographyStyle.tableBorders !== false ? '#f5f5f5 !important' : 'transparent !important',
      fontWeight: '600 !important',
      borderBottom: typographyStyle.tableBorders !== false ? '2px solid #ddd !important' : 'none !important',
    },
    '@media print': {
      '& table th, & table td': {
        border: typographyStyle.tableBorders !== false ? '1px solid #000 !important' : 'none !important',
      },
      '& table th': {
        backgroundColor: typographyStyle.tableBorders !== false ? '#f0f0f0 !important' : 'transparent !important',
        borderBottom: typographyStyle.tableBorders !== false ? '2px solid #000 !important' : 'none !important',
      },
    },
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'background.paper',
      }}
    >
      {letterheadConfig.enabled && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <LetterheadHeader />
        </Box>
      )}

      <Box sx={{ p: 3 }}>
        {content ? (
          <Markdown sanitize={true} sx={typographyStyles}>
            {content}
          </Markdown>
        ) : (
          <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Preview will appear here as you type...
          </Box>
        )}
      </Box>

      {letterheadConfig.enabled && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <LetterheadFooter />
        </Box>
      )}
    </Box>
  );
};
